import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronDown, ArrowLeftRight, Plus, X, Pencil } from 'lucide-react';
import { useUnifiedPrices } from '../../contexts/UnifiedPriceContext';
import { calculateSpreadCost } from '../../constants/spreadConfig';
import { CFD_INSTRUMENTS } from '../../constants/tradingPairs';
import TakeProfitStopLossModal from '../TakeProfitStopLossModal';
import { useListedCfdQuote } from '../../hooks/useListedCfdQuote';
import { getListedCfdInstrument } from '../../../supabase/functions/_shared/cfdInstruments';
import ListedCfdQuoteNotice from '../ListedCfdQuoteNotice';

interface MobileTradingFormProps {
  selectedPair: string;
  usdtBalance: number;
  availableBalance?: number;
  maxAllowedLeverage?: number;
  minAllowedLeverage?: number;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onTrade: (
    symbol: string,
    side: 'long' | 'short',
    amount: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => void;
}

const MobileTradingForm: React.FC<MobileTradingFormProps> = ({
  selectedPair,
  usdtBalance,
  availableBalance,
  maxAllowedLeverage = 100,
  minAllowedLeverage = 1,
  calculateLiquidationPrice,
  onTrade
}) => {
  const { getPriceBySymbol, lastUpdateTime } = useUnifiedPrices();
  const { listing, quoteAvailable, referenceQuote } = useListedCfdQuote(selectedPair);

  const isLeverageLocked = minAllowedLeverage === maxAllowedLeverage;

  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('cross');
  const [leverage, setLeverage] = useState(() => Math.max(20, minAllowedLeverage));
  const [orderType, setOrderType] = useState<'limit' | 'market'>('limit');
  const [inputMode, setInputMode] = useState<'lots' | 'usd'>('lots');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [sliderValue, setSliderValue] = useState(0);
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [stopLoss, setStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [takeProfit, setTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [showSLModal, setShowSLModal] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const [activeSide, setActiveSide] = useState<'long' | 'short'>('long');

  useEffect(() => {
    setLimitPrice('');
    setAmount('');
    setSliderValue(0);
    setStopLoss(null);
    setTakeProfit(null);
  }, [selectedPair]);

  useEffect(() => {
    if (isLeverageLocked) {
      setLeverage(minAllowedLeverage);
    } else if (leverage < minAllowedLeverage) {
      setLeverage(minAllowedLeverage);
    } else if (leverage > maxAllowedLeverage) {
      setLeverage(maxAllowedLeverage);
    }
  }, [minAllowedLeverage, maxAllowedLeverage, isLeverageLocked]);

  const livePairPrice = useMemo(() => {
    return getPriceBySymbol(selectedPair);
  }, [getPriceBySymbol, selectedPair, lastUpdateTime]);

  const isCfdPair = useMemo(() => {
    return CFD_INSTRUMENTS.some(inst => inst.symbol === selectedPair);
  }, [selectedPair]);

  const getLotSize = useCallback((symbol: string): number => {
    const listing = getListedCfdInstrument(symbol);
    if (listing) return listing.lotSize;
    const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
    if (instrument && instrument.type === 'forex') {
      if (symbol.endsWith("JPY")) return 1000;
      return 100000;
    } else if (instrument && instrument.type === 'commodity') {
      switch (symbol) {
        case 'XAG/USD': return 5000;
        case 'XAUUSD':
        case 'XAU/USD': return 100;
        case 'XPTUSD':
        case 'XPT/USD': return 100;
        case 'XPDUSD':
        case 'XPD/USD': return 100;
        case 'NATGAS/USD': return 10000;
        case 'BCO/USD': return 1000;
        case 'WTICO/USD': return 1000;
        case 'CORN/USD': return 5000;
        case 'WHEAT/USD': return 5000;
        case 'SUGAR/USD': return 112000;
        default: return 1;
      }
    } else if (instrument && instrument.type === 'stock') {
      return 100;
    }
    return 1;
  }, []);

  const lotSize = isCfdPair ? getLotSize(selectedPair) : 1;

  const convertUsdToLots = useCallback((usdAmount: number): number => {
    if (!isCfdPair) return usdAmount;
    if (!livePairPrice || livePairPrice <= 0) return 0;
    return usdAmount / (livePairPrice * lotSize);
  }, [isCfdPair, livePairPrice, lotSize]);

  const getLotsFromInput = useCallback((inputValue: string): number => {
    const amt = parseFloat(inputValue) || 0;
    if (!isCfdPair || inputMode === 'lots') return amt;
    return convertUsdToLots(amt);
  }, [isCfdPair, inputMode, convertUsdToLots]);

  const lotsAmount = getLotsFromInput(amount);

  useEffect(() => {
    if (livePairPrice > 0 && !limitPrice) {
      setLimitPrice(livePairPrice.toFixed(5));
    }
  }, [livePairPrice, limitPrice]);

  const balance = availableBalance !== undefined ? availableBalance : usdtBalance;
  const baseAsset = selectedPair.replace('USDT', '').replace('/USD', '');

  const calculateAmountFromSlider = useCallback((percentage: number) => {
    if (percentage <= 0 || livePairPrice <= 0) return '';
    const targetMargin = balance * (percentage / 100);
    if (isCfdPair) {
      const calculatedLots = (targetMargin * leverage) / (livePairPrice * lotSize);
      return calculatedLots.toFixed(5);
    }
    const calculatedAmount = (targetMargin * leverage) / livePairPrice;
    return calculatedAmount.toFixed(6);
  }, [balance, leverage, livePairPrice, isCfdPair, lotSize]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    if (isCfdPair && inputMode === 'usd') {
      setInputMode('lots');
    }
    const newAmount = calculateAmountFromSlider(value);
    setAmount(newAmount);
  };

  const calculateNotionalValue = () => {
    if (isCfdPair) {
      return lotsAmount * livePairPrice * lotSize;
    }
    const amt = parseFloat(amount) || 0;
    return amt * livePairPrice;
  };

  const calculateCost = () => {
    const notional = calculateNotionalValue();
    const margin = notional / leverage;
    if (isCfdPair) {
      const spread = lotsAmount > 0 ? calculateSpreadCost(selectedPair, livePairPrice, lotsAmount, lotSize) : 0;
      return margin + spread;
    }
    const spread = parseFloat(amount) > 0 ? calculateSpreadCost(selectedPair, livePairPrice, parseFloat(amount), 1) : 0;
    return margin + spread;
  };

  const handleTrade = (side: 'long' | 'short') => {
    if (!quoteAvailable || !Number.isFinite(livePairPrice) || livePairPrice <= 0) {
      setErrorMessage('A current quote is unavailable. Please try again when the market is open.');
      return;
    }
    if (listing && orderType === 'limit' && (!Number.isFinite(Number(limitPrice)) || Number(limitPrice) <= 0)) {
      setErrorMessage('Enter a valid USD limit price.');
      return;
    }
    if (isCfdPair) {
      if (!lotsAmount || lotsAmount <= 0) {
        setErrorMessage('Please enter a valid amount');
        return;
      }
      const cost = calculateCost();
      if (cost > balance) {
        setErrorMessage('Insufficient available balance');
        return;
      }
      const price = orderType === 'market' ? livePairPrice : parseFloat(limitPrice) || livePairPrice;
      onTrade(selectedPair, side, lotsAmount * lotSize, leverage, marginType, stopLoss || undefined, takeProfit || undefined, orderType, price);
      setSuccessMessage(`${side === 'long' ? 'Long' : 'Short'} position opened for ${lotsAmount.toFixed(5)} lots`);
    } else {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) {
        setErrorMessage('Please enter a valid amount');
        return;
      }
      const cost = calculateCost();
      if (cost > balance) {
        setErrorMessage('Insufficient available balance');
        return;
      }
      const price = orderType === 'market' ? livePairPrice : parseFloat(limitPrice) || livePairPrice;
      onTrade(selectedPair, side, amt, leverage, marginType, stopLoss || undefined, takeProfit || undefined, orderType, price);
      setSuccessMessage(`${side === 'long' ? 'Buy/Long' : 'Sell/Short'} order placed successfully`);
    }
    setAmount('');
    setSliderValue(0);
    setStopLoss(null);
    setTakeProfit(null);
  };

  const openSLModal = (side: 'long' | 'short') => {
    setActiveSide(side);
    setShowSLModal(true);
  };

  const openTPModal = (side: 'long' | 'short') => {
    setActiveSide(side);
    setShowTPModal(true);
  };

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const leverageOptions = [1, 2, 3, 5, 10, 20, 25, 50, 75, 100, 125].filter(l => l >= minAllowedLeverage && l <= maxAllowedLeverage);
  const sliderMarks = [0, 25, 50, 75, 100];

  return (
    <div className="flex flex-col bg-white p-4">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setMarginType(marginType === 'cross' ? 'isolated' : 'cross')}
          className="flex-1 py-2.5 bg-gray-100 text-gray-900 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
        >
          {marginType === 'cross' ? 'Cross' : 'Isolated'}
        </button>
        {isLeverageLocked ? (
          <span className="flex-1 py-2.5 bg-gray-200 text-gray-500 text-sm font-medium rounded text-center cursor-default">
            {leverage}x
          </span>
        ) : (
          <button
            onClick={() => setShowLeverageModal(true)}
            className="flex-1 py-2.5 bg-gray-100 text-gray-900 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
          >
            {leverage}x
          </button>
        )}
        <button className="p-2.5 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
          <ArrowLeftRight size={16} className="text-gray-500" />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setOrderType('limit')}
            className={`px-4 py-2 text-sm font-medium ${
              orderType === 'limit'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
          >
            Limit
          </button>
          <button
            onClick={() => setOrderType('market')}
            className={`px-4 py-2 text-sm font-medium ${
              orderType === 'market'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
          >
            Market
          </button>
        </div>
        {isCfdPair && (
          <div className="flex ml-auto p-0.5 bg-gray-100 rounded-lg">
            <button
              onClick={() => setInputMode('lots')}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                inputMode === 'lots'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Lots
            </button>
            <button
              onClick={() => setInputMode('usd')}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                inputMode === 'usd'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              USD
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span>Avbl</span>
          <span className="text-gray-900">- USDT</span>
          <ArrowLeftRight size={12} className="text-gray-500" />
        </div>
        <span className="text-gray-900 text-xs font-medium">{balance.toFixed(2)}</span>
      </div>

      {orderType === 'limit' && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block">Price {listing ? '(USD)' : ''}</label>
          <div className="flex items-center bg-gray-100 rounded overflow-hidden">
            <input
              type="text"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              className="flex-1 px-3 py-3 bg-transparent text-gray-900 text-sm font-mono focus:outline-none"
              placeholder="0.00000"
            />
            <span className="px-3 text-gray-500 text-sm">USDT</span>
            <button
              onClick={() => {
                if (livePairPrice > 0) setLimitPrice(livePairPrice.toFixed(5));
              }}
              className="px-3 py-3 bg-blue-600 text-white text-sm font-medium"
            >
              BBO
            </button>
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="text-xs text-gray-500 mb-1 block">
          {isCfdPair ? `Size (${inputMode === 'lots' ? 'Lots' : 'USD'})` : 'Size'}
        </label>
        <div className="flex items-center bg-gray-100 rounded overflow-hidden">
          <input
            type="text"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setSliderValue(0);
            }}
            className="flex-1 px-3 py-3 bg-transparent text-gray-900 text-sm font-mono focus:outline-none"
            placeholder={isCfdPair && inputMode === 'lots' ? "0.01" : "0.000000"}
          />
          <span className="px-3 py-3 text-gray-900 text-sm">
            {isCfdPair ? (inputMode === 'lots' ? 'LOTS' : 'USD') : baseAsset}
          </span>
        </div>
        {isCfdPair && inputMode === 'usd' && amount && lotsAmount > 0 && (
          <div className="mt-1 text-xs text-gray-500">
            = {lotsAmount.toFixed(5)} lots
          </div>
        )}
      </div>

      <div className="mb-4 px-1">
        <input
          type="range"
          min="0"
          max="100"
          value={sliderValue}
          onChange={(e) => handleSliderChange(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #2563eb 0%, #2563eb ${sliderValue}%, #e5e7eb ${sliderValue}%, #e5e7eb 100%)`
          }}
        />
        <div className="flex justify-between mt-2">
          {sliderMarks.map(mark => (
            <button
              key={mark}
              onClick={() => handleSliderChange(mark)}
              className={`w-3 h-3 rotate-45 border-2 transition-colors ${
                sliderValue >= mark
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-gray-200 border-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {amount && (isCfdPair ? lotsAmount > 0 : parseFloat(amount) > 0) && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => openSLModal('long')}
            className={`flex-1 py-2.5 text-xs font-medium rounded transition-all flex items-center justify-center gap-1.5 ${
              stopLoss
                ? 'bg-red-100 text-red-600 border border-red-200'
                : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}
          >
            {stopLoss ? (
              <>
                <Pencil size={12} />
                SL: ${stopLoss.trigger_price.toFixed(2)}
                <X
                  size={12}
                  className="ml-1"
                  onClick={(e) => { e.stopPropagation(); setStopLoss(null); }}
                />
              </>
            ) : (
              <>
                <Plus size={12} />
                Stop Loss
              </>
            )}
          </button>
          <button
            onClick={() => openTPModal('long')}
            className={`flex-1 py-2.5 text-xs font-medium rounded transition-all flex items-center justify-center gap-1.5 ${
              takeProfit
                ? 'bg-green-100 text-green-600 border border-green-200'
                : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}
          >
            {takeProfit ? (
              <>
                <Pencil size={12} />
                TP: ${takeProfit.trigger_price.toFixed(2)}
                <X
                  size={12}
                  className="ml-1"
                  onClick={(e) => { e.stopPropagation(); setTakeProfit(null); }}
                />
              </>
            ) : (
              <>
                <Plus size={12} />
                Take Profit
              </>
            )}
          </button>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => handleTrade('long')}
          disabled={!quoteAvailable || livePairPrice <= 0}
          className="flex-1 py-3.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded transition-colors"
        >
          Buy/Long
        </button>
        <button
          onClick={() => handleTrade('short')}
          disabled={!quoteAvailable || livePairPrice <= 0}
          className="flex-1 py-3.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded transition-colors"
        >
          Sell/Short
        </button>
      </div>

      {listing && (
        <ListedCfdQuoteNotice quoteAvailable={quoteAvailable} referenceQuote={referenceQuote} />
      )}
      {(errorMessage || successMessage) && (
        <div className={`p-3 rounded mb-4 text-sm ${
          errorMessage ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
        }`}>
          {errorMessage || successMessage}
        </div>
      )}

      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Value (USDT)</span>
          <span className="text-gray-900 font-mono">{calculateNotionalValue().toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Cost (USDT)</span>
          <span className="text-gray-900 font-mono">{calculateCost().toFixed(2)}</span>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4 mt-3">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <div className="text-gray-500 mb-1">Liq Price (Long)</div>
            <div className="text-red-500 font-mono">
              {livePairPrice > 0 ? calculateLiquidationPrice('long', livePairPrice, leverage, marginType).toFixed(2) : '--'} USDT
            </div>
          </div>
          <div className="text-right">
            <div className="text-gray-500 mb-1">Liq Price (Short)</div>
            <div className="text-red-500 font-mono">
              {livePairPrice > 0 ? calculateLiquidationPrice('short', livePairPrice, leverage, marginType).toFixed(2) : '--'} USDT
            </div>
          </div>
        </div>
      </div>

      {!isLeverageLocked && showLeverageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="w-full bg-white rounded-t-2xl p-4 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-900 font-semibold">Adjust Leverage</h3>
              <button onClick={() => setShowLeverageModal(false)} className="text-gray-500">
                Close
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {leverageOptions.map(lev => (
                <button
                  key={lev}
                  onClick={() => {
                    setLeverage(lev);
                    setShowLeverageModal(false);
                  }}
                  className={`py-3 rounded text-sm font-medium transition-colors ${
                    leverage === lev
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {lev}x
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <TakeProfitStopLossModal
        isOpen={showSLModal}
        onClose={() => setShowSLModal(false)}
        type="stopLoss"
        side={activeSide}
        entryPrice={livePairPrice}
        amount={isCfdPair ? lotsAmount : (parseFloat(amount) || 0)}
        leverage={leverage}
        lotSize={isCfdPair ? lotSize : undefined}
        onConfirm={(price, type, execPrice) => {
          if (price > 0) {
            setStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
          } else {
            setStopLoss(null);
          }
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showTPModal}
        onClose={() => setShowTPModal(false)}
        type="takeProfit"
        side={activeSide}
        entryPrice={livePairPrice}
        amount={isCfdPair ? lotsAmount : (parseFloat(amount) || 0)}
        leverage={leverage}
        lotSize={isCfdPair ? lotSize : undefined}
        onConfirm={(price, type, execPrice) => {
          if (price > 0) {
            setTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
          } else {
            setTakeProfit(null);
          }
        }}
      />
    </div>
  );
};

export default MobileTradingForm;
